import { partials } from "@shopify/partial-rendering";
import { transition } from "view-transition";

// <s-cart-recommendations> refreshes its {% partial %} region with Shopify recommendations
// for the first product in the cart whenever the cart drawer opens or its lines change.
// The anchor product id is read from [data-cart-anchor-product], which lives inside the
// cart-drawer partial so it stays current after every cart mutation.
class CartRecommendations extends HTMLElement {
  #controller = null;
  #observers = [];
  #anchor = "";

  connectedCallback() {
    const drawer = document.getElementById(this.dataset.drawer || "cart-drawer");
    if (!drawer || !this.dataset.url || !this.dataset.partial) return;

    const openObserver = new MutationObserver(() => {
      if (drawer.hasAttribute("open")) this.#refresh(drawer);
    });
    openObserver.observe(drawer, { attributes: true, attributeFilter: ["open"] });

    const linesObserver = new MutationObserver(() => {
      if (drawer.hasAttribute("open")) this.#refresh(drawer);
    });
    const lines = drawer.querySelector("#cart-drawer-items");
    if (lines) linesObserver.observe(lines, { childList: true, subtree: true });

    this.#observers = [openObserver, linesObserver];
    if (drawer.hasAttribute("open")) this.#refresh(drawer);
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#controller?.abort();
  }

  async #refresh(drawer) {
    // The signature changes whenever cart products change, so flies just added drop out of the list.
    const marker = drawer.querySelector("[data-cart-anchor-product]");
    const anchor = marker?.dataset.cartAnchorProduct || "";
    const signature = marker?.dataset.cartSignature || anchor;
    if (signature === this.#anchor) return;
    this.#anchor = signature;
    this.#controller?.abort();

    // Remember which suggestion row held focus so it can be restored after the list re-renders.
    const focusIndex = this.#focusedRowIndex();

    if (!anchor) {
      this.hidden = true;
      this.#restoreFocus(drawer, focusIndex);
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const url = `${this.dataset.url}&product_id=${encodeURIComponent(anchor)}`;

    this.setAttribute("aria-busy", "true");
    try {
      const html = await partials.fetch(this.dataset.partial, { url, signal });
      await transition(() => partials.apply(html));
      this.#syncVisibility();
      this.#restoreFocus(drawer, focusIndex);
    } catch (error) {
      if (error.name !== "AbortError") {
        this.hidden = true;
        this.#restoreFocus(drawer, focusIndex);
      }
    } finally {
      this.removeAttribute("aria-busy");
      this.#controller = null;
    }
  }

  #focusedRowIndex() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return -1;
    const rows = [...this.querySelectorAll("li")];
    return rows.findIndex((row) => row.contains(active));
  }

  // The added fly drops out of the list, so focus moves to the suggestion now in its place,
  // then the last remaining one, then the drawer's close button.
  #restoreFocus(drawer, index) {
    if (index < 0) return;
    if (this.contains(document.activeElement) && document.activeElement !== document.body) return;

    const rows = this.hidden ? [] : [...this.querySelectorAll("li")];
    const row = rows[Math.min(index, rows.length - 1)];
    const target =
      row?.querySelector("button[name='add']") ||
      row?.querySelector("a[href]") ||
      drawer.querySelector("button[command='close']");
    target?.focus({ preventScroll: false });
  }

  #syncVisibility() {
    const region = partials.getAll(this).find(({ name }) => name === this.dataset.partial);
    const content = region?.range.cloneContents();
    this.hidden = !content || !content.querySelector("li");
  }
}

if (!customElements.get("s-cart-recommendations")) {
  customElements.define("s-cart-recommendations", CartRecommendations);
}
